import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Heart, QrCode, Sparkles, Users, Landmark, ChevronDown, BadgeCheck, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const rp = (a) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(a || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const InfaqManager = () => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [qrProgram, setQrProgram] = useState(null);
  const [registeredAccounts, setRegisteredAccounts] = useState([]);
  const [rekeningMode, setRekeningMode] = useState('default');
  const [rekeningManual, setRekeningManual] = useState({ bank_name: '', account_number: '', account_holder: '' });
  const [formData, setFormData] = useState({
    title: '', description: '', target_amount: 0, current_amount: 0, image_url: '', category: 'sosial',
    program_type: 'infaq', ticket_price: '', nominal_per_jiwa: '', prize: '',
    rekening_id: null, rekening_manual: null,
  });
  const [donationsModal, setDonationsModal] = useState(null); // {program}
  const [donations, setDonations] = useState([]);
  const [loadingDonations, setLoadingDonations] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // donation object to delete
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchPrograms();
    axios.get(`${API_URL}/api/mosque-accounts`, { withCredentials: true })
      .then(r => setRegisteredAccounts(r.data)).catch(() => {});
  }, []);

  const fetchPrograms = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/infaq`, { withCredentials: true, params: { mosque_id: user?.mosque_id } });
      setPrograms(data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDonationsModal = async (program) => {
    setDonationsModal(program);
    setLoadingDonations(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/infaq/${program.id}/donations`, { withCredentials: true });
      setDonations(data);
    } catch {
      toast.error('Gagal memuat daftar donasi');
      setDonations([]);
    } finally {
      setLoadingDonations(false);
    }
  };

  const handleVerify = async (donationId) => {
    try {
      await axios.post(`${API_URL}/api/infaq/donations/${donationId}/verify`, {}, { withCredentials: true });
      toast.success('Donasi berhasil diverifikasi!');
      setDonations(prev => prev.map(d => d.id === donationId ? { ...d, is_verified: true } : d));
      // Refresh programs to update current_amount
      fetchPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memverifikasi');
    }
  };

  const handleDeleteDonation = async (donation) => {
    setDeletingId(donation.id);
    try {
      await axios.delete(`${API_URL}/api/infaq/donations/${donation.id}`, { withCredentials: true });
      toast.success('Donasi berhasil dihapus');
      setConfirmDelete(null);
      setDonations(prev => prev.filter(d => d.id !== donation.id));
      // Refresh programs to update current_amount if was verified
      if (donation.is_verified) fetchPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus donasi');
    } finally {
      setDeletingId(null);
    }
  };

  const buildRekeningPayload = () => {
    if (rekeningMode === 'default') return { rekening_id: null, rekening_manual: null };
    if (rekeningMode === 'manual') {
      return { rekening_id: null, rekening_manual: rekeningManual };
    }
    // mode = 'id:<account_id>'
    const accId = rekeningMode.replace('id:', '');
    return { rekening_id: accId, rekening_manual: null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rkPayload = buildRekeningPayload();
    const raw = { ...formData, ...rkPayload };
    // Convert empty strings to null for optional numeric fields
    const payload = {
      ...raw,
      ticket_price: raw.ticket_price === '' || raw.ticket_price === undefined ? null : raw.ticket_price,
      nominal_per_jiwa: raw.nominal_per_jiwa === '' || raw.nominal_per_jiwa === undefined ? null : raw.nominal_per_jiwa,
    };
    try {
      if (editingProgram) {
        await axios.put(`${API_URL}/api/infaq/${editingProgram.id}`, payload, { withCredentials: true });
        toast.success('Program berhasil diupdate!');
      } else {
        await axios.post(`${API_URL}/api/infaq`, payload, { withCredentials: true });
        toast.success('Program berhasil ditambahkan!');
      }
      setShowModal(false);
      setEditingProgram(null);
      resetForm();
      fetchPrograms();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Gagal menyimpan program');
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus program ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/infaq/${id}`, { withCredentials: true });
      toast.success('Program berhasil dihapus!');
      fetchPrograms();
    } catch (error) {
      toast.error('Gagal menghapus program');
    }
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      title: program.title || '',
      description: program.description || '',
      target_amount: program.target_amount || 0,
      current_amount: program.current_amount || 0,
      image_url: program.image_url || '',
      category: program.category || 'sosial',
      program_type: program.program_type || 'infaq',
      ticket_price: program.ticket_price || '',
      nominal_per_jiwa: program.nominal_per_jiwa || '',
      prize: program.prize || '',
    });
    // Restore rekening mode
    if (program.rekening_id) {
      setRekeningMode(`id:${program.rekening_id}`);
    } else if (program.rekening_manual?.account_number) {
      setRekeningMode('manual');
      setRekeningManual(program.rekening_manual);
    } else {
      setRekeningMode('default');
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', target_amount: 0, current_amount: 0, image_url: '', category: 'sosial',
      program_type: 'infaq', ticket_price: '', nominal_per_jiwa: '', prize: '', rekening_id: null, rekening_manual: null });
    setRekeningMode('default');
    setRekeningManual({ bank_name: '', account_number: '', account_holder: '' });
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">Program Infaq</h2>
          <p className="text-sm font-body text-text-secondary mt-1">
            Kelola program donasi dan infaq masjid
          </p>
        </div>
        <button
          data-testid="add-infaq-btn"
          onClick={() => {
            setEditingProgram(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Tambah Program
        </button>
      </div>

      {programs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Heart className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-text-secondary font-body mb-4">Belum ada program infaq</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-emerald-700 font-body font-medium hover:underline"
          >
            Tambah program pertama
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program) => {
            const progress = (program.current_amount / program.target_amount) * 100;
            return (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
                data-testid={`infaq-item-${program.id}`}
              >
                {program.image_url && (
                  <div
                    className="h-32 bg-cover bg-center"
                    style={{ backgroundImage: `url('${program.image_url}')` }}
                  />
                )}
                <div className="p-5">
                  <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium mb-2">
                    {program.category}
                  </span>
                  <h3 className="text-lg font-body font-semibold text-text-primary mb-2">
                    {program.title}
                  </h3>
                  <p className="text-sm font-body text-text-secondary mb-4 line-clamp-2">
                    {program.description}
                  </p>

                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-body text-text-secondary">Progress</span>
                      <span className="text-xs font-body font-semibold text-emerald-700">
                        {progress.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-xs font-body text-text-secondary">Terkumpul</p>
                      <p className="text-sm font-body font-bold text-emerald-800">
                        {formatRupiah(program.current_amount)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-body text-text-secondary">Target</p>
                      <p className="text-sm font-body font-semibold text-text-primary">
                        {formatRupiah(program.target_amount)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      data-testid={`qr-infaq-${program.id}`}
                      onClick={() => setQrProgram(program)}
                      className="flex items-center justify-center px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl"
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`donors-infaq-${program.id}`}
                      onClick={() => openDonationsModal(program)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl font-body text-xs font-medium"
                    >
                      <BadgeCheck className="w-4 h-4" /> Donatur
                    </button>
                    <button
                      data-testid={`edit-infaq-${program.id}`}
                      onClick={() => handleEdit(program)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl font-body font-medium transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      data-testid={`delete-infaq-${program.id}`}
                      onClick={() => handleDelete(program.id)}
                      className="flex items-center justify-center px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal Form */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-heading font-bold text-primary-dark">
                  {editingProgram ? 'Edit Program' : 'Tambah Program Baru'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingProgram(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">Jenis Program *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[['infaq', 'Infaq/Donasi'], ['umroh', 'Infaq Umroh (Premium)'], ['zakat', 'Zakat']].map(([v, l]) => (
                      <button type="button" key={v} data-testid={`infaq-type-${v}`} onClick={() => setFormData({ ...formData, program_type: v })}
                        className={`px-3 py-2 rounded-xl text-xs font-body font-medium border ${formData.program_type === v ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-stone-600 border-stone-200'}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {formData.program_type === 'umroh' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <div>
                      <label className="block text-sm font-body font-medium text-text-primary mb-2">Harga per Tiket Undian (Rp) *</label>
                      <input type="number" data-testid="infaq-ticket-price-input" value={formData.ticket_price} onChange={(e) => setFormData({ ...formData, ticket_price: parseFloat(e.target.value) })} className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none font-body" placeholder="100000" />
                    </div>
                    <div>
                      <label className="block text-sm font-body font-medium text-text-primary mb-2">Hadiah</label>
                      <input data-testid="infaq-prize-input" value={formData.prize} onChange={(e) => setFormData({ ...formData, prize: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none font-body" placeholder="1 Paket Umroh" />
                    </div>
                  </div>
                )}
                {formData.program_type === 'zakat' && (
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <label className="block text-sm font-body font-medium text-text-primary mb-2">Nominal Zakat per Jiwa (Rp) *</label>
                    <input type="number" data-testid="infaq-nominal-jiwa-input" value={formData.nominal_per_jiwa} onChange={(e) => setFormData({ ...formData, nominal_per_jiwa: parseFloat(e.target.value) })} className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none font-body" placeholder="45000" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Judul Program *
                  </label>
                  <input
                    type="text"
                    data-testid="infaq-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: Santunan Anak Yatim"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Deskripsi *
                  </label>
                  <textarea
                    data-testid="infaq-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    rows={3}
                    placeholder="Jelaskan detail program..."
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-body font-medium text-text-primary mb-2">
                      Target Dana (Rp) *
                    </label>
                    <input
                      type="number"
                      data-testid="infaq-target-input"
                      value={formData.target_amount}
                      onChange={(e) => setFormData({ ...formData, target_amount: parseFloat(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                      placeholder="50000000"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-body font-medium text-text-primary mb-2">
                      Dana Terkumpul (Rp)
                    </label>
                    <input
                      type="number"
                      data-testid="infaq-current-input"
                      value={formData.current_amount}
                      onChange={(e) => setFormData({ ...formData, current_amount: parseFloat(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Kategori *
                  </label>
                  <select
                    data-testid="infaq-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    required
                  >
                    <option value="sosial">Sosial</option>
                    <option value="pembangunan">Pembangunan</option>
                    <option value="pendidikan">Pendidikan</option>
                    <option value="kesehatan">Kesehatan</option>
                    <option value="operasional">Operasional</option>
                  </select>
                </div>

                <div>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Gambar Program"
                  />
                </div>

                {/* Rekening Tujuan */}
                <div className="bg-stone-50 rounded-xl p-4 space-y-3 border border-stone-200">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-emerald-700" />
                    <label className="text-sm font-medium text-stone-700">Rekening Tujuan</label>
                  </div>
                  <select
                    data-testid="infaq-rekening-select"
                    value={rekeningMode}
                    onChange={e => {
                      setRekeningMode(e.target.value);
                      if (e.target.value !== 'manual') setRekeningManual({ bank_name: '', account_number: '', account_holder: '' });
                    }}
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 outline-none text-sm bg-white"
                  >
                    <option value="default">-- Rekening Utama Masjid (default) --</option>
                    {registeredAccounts.map(acc => (
                      <option key={acc.id} value={`id:${acc.id}`}>
                        {acc.is_primary ? '⭐ ' : ''}{acc.bank_name} · {acc.account_number} (a/n {acc.account_holder})
                      </option>
                    ))}
                    <option value="manual">+ Input Rekening Baru (manual)</option>
                  </select>
                  {rekeningMode === 'manual' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                      <div>
                        <label className="block text-xs text-stone-500 mb-1">Nama Bank</label>
                        <input value={rekeningManual.bank_name} onChange={e => setRekeningManual(r => ({ ...r, bank_name: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none" placeholder="BSI / BNI / Mandiri" />
                      </div>
                      <div>
                        <label className="block text-xs text-stone-500 mb-1">Nomor Rekening</label>
                        <input value={rekeningManual.account_number} onChange={e => setRekeningManual(r => ({ ...r, account_number: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none" placeholder="1234567890" />
                      </div>
                      <div>
                        <label className="block text-xs text-stone-500 mb-1">Atas Nama</label>
                        <input value={rekeningManual.account_holder} onChange={e => setRekeningManual(r => ({ ...r, account_holder: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none" placeholder="Nama Pemilik" />
                      </div>
                    </div>
                  )}
                  {rekeningMode !== 'default' && rekeningMode !== 'manual' && (
                    <p className="text-xs text-emerald-700">
                      Rekening ini akan ditampilkan di halaman donasi program ini.
                    </p>
                  )}
                  {rekeningMode === 'default' && (
                    <p className="text-xs text-stone-500">
                      Program ini akan menggunakan rekening utama masjid yang tersedia saat ini.
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProgram(null);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-body font-medium transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    data-testid="infaq-submit-btn"
                    className="flex-1 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
                  >
                    {editingProgram ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Donations Verification Modal */}
      <AnimatePresence>
        {donationsModal && (
          <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50" data-testid="donations-verify-modal">
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden"
              style={{ maxHeight: '90vh' }}
            >
              <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500">{donationsModal.title}</p>
                  <h3 className="text-lg font-heading font-bold text-primary-dark">Verifikasi Donatur</h3>
                </div>
                <button
                  onClick={() => setDonationsModal(null)}
                  className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center"
                  data-testid="donations-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: '70vh' }}>
                {loadingDonations ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : donations.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-500 font-body text-sm">Belum ada donasi masuk</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {donations.map((d) => (
                      <div
                        key={d.id}
                        className={`p-4 rounded-2xl border transition-colors ${d.is_verified ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-stone-200'}`}
                        data-testid={`verify-item-${d.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-700 to-[#022C22] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(d.donor_name || 'H').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-sm text-[#1C1917] truncate">{d.donor_name || 'Hamba Allah'}</span>
                              {d.is_verified && <BadgeCheck className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                            </div>
                            <p className="text-xs text-stone-500">{fmtDate(d.created_at)}</p>
                            <p className="text-sm font-bold text-emerald-700">{rp(d.amount)}</p>
                          </div>
                          {d.is_verified ? (
                            <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1 bg-emerald-100 px-3 py-1.5 rounded-xl flex-shrink-0">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Terverifikasi
                            </span>
                          ) : (
                            <button
                              onClick={() => handleVerify(d.id)}
                              className="text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-xl flex-shrink-0 transition-colors flex items-center gap-1"
                              data-testid={`verify-btn-${d.id}`}
                            >
                              <BadgeCheck className="w-3.5 h-3.5" /> Setujui
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmDelete(d)}
                            className="p-1.5 rounded-xl text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                            data-testid={`delete-donation-btn-${d.id}`}
                            title="Hapus donasi"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {/* Proof image thumbnail */}
                        {d.proof_image_path && (
                          <div className="mt-3 ml-13">
                            <p className="text-xs text-stone-400 mb-1.5">Bukti Transfer:</p>
                            <button
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.proof_image_path}`, '_blank')}
                              className="relative group rounded-xl overflow-hidden border border-stone-200"
                              style={{ width: 80, height: 64, display: 'block' }}
                              data-testid={`proof-thumb-${d.id}`}
                              title="Klik untuk lihat bukti lengkap"
                            >
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.proof_image_path}`}
                                alt="Bukti Transfer"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <svg className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal: Hapus Donasi */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
            data-testid="confirm-delete-overlay"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 340, damping: 30 }}
              className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full"
              data-testid="confirm-delete-modal"
            >
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-heading font-bold text-[#1C1917] text-lg text-center mb-2">
                Hapus Donasi?
              </h3>
              <p className="text-sm text-[#57534E] text-center mb-1">
                Apakah Anda yakin ingin menghapus data donasi <span className="font-semibold text-[#1C1917]">{confirmDelete.donor_name}</span> sebesar <span className="font-semibold text-emerald-700">{rp(confirmDelete.amount)}</span>?
              </p>
              <p className="text-xs text-red-500 text-center mb-6">
                {confirmDelete.is_verified
                  ? 'Donasi ini sudah terverifikasi. Saldo program akan dikurangi otomatis.'
                  : 'Tindakan ini tidak dapat dibatalkan.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 py-3 rounded-2xl border border-stone-200 text-stone-600 text-sm font-medium hover:bg-stone-50 transition-colors"
                  data-testid="cancel-delete-btn"
                >
                  Batal
                </button>
                <button
                  onClick={() => handleDeleteDonation(confirmDelete)}
                  disabled={deletingId === confirmDelete.id}
                  className="flex-1 py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                  data-testid="confirm-delete-donation-btn"
                >
                  {deletingId === confirmDelete.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Menghapus...
                    </span>
                  ) : 'Ya, Hapus'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InfaqManager;